import { useState, useEffect } from 'react';
import { GRID_MAT } from '../spizirna/materialGrid';
import { nejhorsiMarze } from './prehledGrid';

/**
 * Sloupcová mřížka širokého přehledu ingrediencí (ADD) — protějšek
 * `prehledGrid.js` (produkty) a `spizirna/materialGrid.js` (suroviny), se
 * stejnými pravidly (standard-rozvrzeni.md §3): šířky na jednom místě pro
 * hlavičku, položky i skupiny; volnou šířku bere název; číselné sloupce mají
 * pevnou šířku a sedí vpravo jako blok.
 *
 * Ingredience se prodává jako příplatek k jídlu, takže mřížka nese, podle čeho
 * se hlídá: v jakých skupinách se nabízí, kolik stojí a kolik na ní zbude,
 * jaké alergeny host přidá k jídlu, a jak čerstvá je revize receptury.
 */
export const GRID_ING = {
    uchyt: 44,          // chevron skupiny (položka tu nic nemá, drží zarovnání)
    nahled: 44,         // ikona
    polozkaMin: 240,    // název + meta — pod tohle nesmí spadnout
    polozkaRust: 3,     // váha, jakou název bere volnou šířku (alergeny mají 1)
    skupiny: 190,       // ve kterých skupinách se nabízí
    priplatek: 176,     // náklad → příplatek s DPH
    marze: 84,          // nejhorší marže z kanálů
    alergeny: GRID_MAT.alergeny, // sdílená AlergenyBunka surovin
    revize: 104,
    stav: 76,           // stejná šířka jako receptura — sdílená StavBunka
    mezera: 14,
};

/**
 * Příplatek, jak ho host platí (s DPH), přes kanály. Cena se dá zadat pro
 * kanál zvlášť, takže to může být rozpětí. `null` = příplatek nemá cenu.
 */
export const priplatekRozpeti = (rec) => {
    const ceny = Object.values(rec?.marze_kanaly || {})
        .map(k => Number(k?.cena_s_dph) || 0)
        .filter(c => c > 0);
    if (!ceny.length) return null;
    return { min: Math.min(...ceny), max: Math.max(...ceny) };
};

/** Nejvyšší cena bez DPH z kanálů — podíl nákladu v proužku pod čísly. */
export const priplatekBezDph = (rec) => {
    const ceny = Object.values(rec?.marze_kanaly || {}).map(k => Number(k?.cena) || 0).filter(c => c > 0);
    return ceny.length ? Math.max(...ceny) : null;
};

/** Marže ingredience = nejhorší kanál (stejně jako práh v přehledu produktů). */
export const marzeIngredience = (rec) => nejhorsiMarze(rec?.marze_kanaly);

/**
 * 'obsahuje' | 'bez' | 'nevyplneno'. Alergeny ingredience plynou z její
 * receptury — bez surovin v receptuře se o nich nedá nic říct.
 */
export const stavAlergenuIngredience = (rec) => {
    if ((rec?.alergeny || []).length) return 'obsahuje';
    return (rec?.kusovnik_count || 0) > 0 ? 'bez' : 'nevyplneno';
};

/**
 * Které sloupce se ještě vejdou. Na užším monitoru se odspoda odebírají ty
 * nejméně důležité: revize → skupiny (nese je i hlavička skupiny) → náhled.
 * Příplatek, marže, alergeny a stav zůstávají vždy.
 */
export function useViditelneSloupceIngredienci(zapnuto = true) {
    const spocitej = () => {
        const w = window.innerWidth;
        return {
            nahled: w >= 1100,
            skupiny: w >= 1280,
            revize: w >= 1440,
        };
    };
    const [sloupce, setSloupce] = useState(spocitej);
    useEffect(() => {
        if (!zapnuto) return;
        const h = () => setSloupce(spocitej());
        h();
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, [zapnuto]);
    return sloupce;
}
